// pages/admin/InstructorTable.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Chip,
  Stack,
  Button,
  TextField,
  InputAdornment,
  Fade,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  styled,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Home,
  People,
  Search,
  Add,
  Delete,
  CheckCircle,
  School,
  Email,
  Verified,
  Block,
  Clear,
  ArrowForward,
  TrendingUp,
  EmojiEvents,
  PersonAdd,
  Edit,
} from "@mui/icons-material";
import { getData, postData } from "../../services/Request";
import { useLoader } from "../../context/LoaderContext";
import Swal from "sweetalert2";
import AdminSideBar from "./components/AdminSideBar";

const rowsPerPageOptions = [10, 20, 30, 50];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 14,
  color: "#ffffff",
  backgroundColor: "#45B7D1",
  padding: "12px 16px",
  "&:first-of-type": {
    borderTopLeftRadius: 8,
  },
  "&:last-of-type": {
    borderTopRightRadius: 8,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(247, 248, 242, 0.5)",
  },
  "&:hover": {
    backgroundColor: "rgba(69, 183, 209, 0.04)",
    transition: "background-color 0.2s ease",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const InstructorTable = () => {
  const nav = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const [userInfo, setUserInfo] = useState(null);
  const [userData, setUserData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        if (data.usertype !== "Admin") {
          nav("/admin");
        } else {
          setUserInfo(data);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        nav("/admin");
      }
    } else {
      nav("/admin");
    }
  }, [nav]);

  // Debounce search term
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchTerm]);

  // Fetch instructors with search and pagination
  const fetchInstructors = useCallback(() => {
    showLoader();
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearchTerm.trim(),
    };

    getData("allTeachers", params)
      .then((response) => {
        const users = response.users || [];
        // Filter only instructors
        const instructors = users.filter(
          (user) =>
            user.usertype === "Instructor" || user.usertype === "instuctor",
        );
        setUserData(instructors);
        setTotalCount(response.totalCount || instructors.length);
      })
      .catch((error) => {
        console.error(error);
        setUserData([]);
        setTotalCount(0);
      })
      .finally(() => {
        hideLoader();
      });
  }, [page, rowsPerPage, debouncedSearchTerm, showLoader, hideLoader]);

  useEffect(() => {
    fetchInstructors();
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusActive = (id) => {
    Swal.fire({
      title: "Do you want to activate this instructor?",
      text: "This instructor will be able to access the platform.",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes, Activate",
      denyButtonText: "No",
      confirmButtonColor: "#2BAD65",
      denyButtonColor: "#FF6B6B",
      background: "#ffffff",
      color: "#1a1a1a",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Activated!",
          text: "Instructor has been activated successfully.",
          icon: "success",
          confirmButtonColor: "#2BAD65",
          background: "#ffffff",
          color: "#1a1a1a",
        });
        postData("updateTeacher", { id: id, active: true }).then(() => {
          fetchInstructors();
        });
      }
    });
  };

  const handleStatusInactive = (id) => {
    Swal.fire({
      title: "Do you want to deactivate this instructor?",
      text: "This instructor will lose access to the platform.",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes, Deactivate",
      denyButtonText: "No",
      confirmButtonColor: "#FF6B6B",
      denyButtonColor: "#2BAD65",
      background: "#ffffff",
      color: "#1a1a1a",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deactivated!",
          text: "Instructor has been deactivated successfully.",
          icon: "success",
          confirmButtonColor: "#2BAD65",
          background: "#ffffff",
          color: "#1a1a1a",
        });
        postData("updateTeacher", { id: id, active: false }).then(() => {
          fetchInstructors();
        });
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete Instructor",
      text: "Are you sure you want to delete this instructor? This action cannot be undone.",
      icon: "warning",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      denyButtonText: "Cancel",
      confirmButtonColor: "#FF6B6B",
      denyButtonColor: "#2BAD65",
      background: "#ffffff",
      color: "#1a1a1a",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleted!",
          text: "Instructor has been deleted successfully.",
          icon: "success",
          confirmButtonColor: "#2BAD65",
          background: "#ffffff",
          color: "#1a1a1a",
        });
        postData("deleteUser", { id: id }).then(() => {
          fetchInstructors();
        });
      }
    });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return `${first}${last}`.toUpperCase() || "I";
  };

  const stats = [
    {
      icon: <School sx={{ fontSize: 24 }} />,
      label: "Total Instructors",
      value: totalCount || "0",
      color: "#45B7D1",
      bgColor: "rgba(69, 183, 209, 0.08)",
    },
    {
      icon: <Verified sx={{ fontSize: 24 }} />,
      label: "Active Instructors",
      value: userData.filter((u) => u.status === 1).length,
      color: "#2BAD65",
      bgColor: "rgba(43, 173, 101, 0.08)",
    },
    {
      icon: <Block sx={{ fontSize: 24 }} />,
      label: "Inactive Instructors",
      value: userData.filter((u) => u.status === 0).length,
      color: "#FF6B6B",
      bgColor: "rgba(255, 107, 107, 0.08)",
    },
    {
      icon: <People sx={{ fontSize: 24 }} />,
      label: "Total Students",
      value: "2,500",
      color: "#FFD93D",
      bgColor: "rgba(255, 217, 61, 0.08)",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#F7F8F2",
      }}
    >
      {/* Admin Sidebar */}
      <Box
        sx={{
          width: { xs: 0, md: 280 },
          flexShrink: 0,
          display: { xs: "none", md: "block" },
        }}
      >
        <AdminSideBar />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 2, md: 3 },
          }}
        >
          <Container maxWidth="xl" sx={{ p: 0 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs
                separator={
                  <ArrowForward
                    sx={{ fontSize: 14, color: "rgba(26,26,26,0.2)" }}
                  />
                }
                aria-label="breadcrumb"
                sx={{ mb: 1 }}
              >
                <Link
                  underline="hover"
                  color="rgba(26, 26, 26, 0.4)"
                  href="/admin/dashboard"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.85rem",
                    "&:hover": {
                      color: "#45B7D1",
                    },
                  }}
                >
                  <Home sx={{ fontSize: 16 }} />
                  Home
                </Link>
                <Typography
                  sx={{
                    color: "#45B7D1",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <School sx={{ fontSize: 16 }} />
                  Instructors
                </Typography>
              </Breadcrumbs>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a1a",
                      fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                  >
                    Instructor Management
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(26, 26, 26, 0.4)",
                      mt: 0.5,
                    }}
                  >
                    Manage all instructors and their activity on the platform
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    icon={<School sx={{ fontSize: 16 }} />}
                    label={`Admin: ${userInfo?.firstname || "Admin"}`}
                    sx={{
                      backgroundColor: "rgba(69, 183, 209, 0.08)",
                      color: "#45B7D1",
                      fontWeight: 600,
                      border: "1px solid rgba(69, 183, 209, 0.1)",
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {stats.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Fade in timeout={400 + index * 100}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        backgroundColor: "#ffffff",
                        border: "1px solid rgba(69, 183, 209, 0.04)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 30px rgba(69, 183, 209, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            backgroundColor: stat.bgColor,
                            borderRadius: "50%",
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: stat.color,
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: "#1a1a1a",
                              fontSize: "1.5rem",
                              lineHeight: 1.2,
                            }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "rgba(26, 26, 26, 0.4)",
                              fontSize: "0.75rem",
                            }}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Fade>
                </Grid>
              ))}
            </Grid>

            {/* Search Bar */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(69, 183, 209, 0.04)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TextField
                  placeholder="Search instructors by name or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "rgba(26, 26, 26, 0.3)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClearSearch}
                          size="small"
                          sx={{
                            color: "rgba(26, 26, 26, 0.3)",
                            "&:hover": {
                              color: "#1a1a1a",
                            },
                          }}
                        >
                          <Clear sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      "& fieldset": {
                        borderColor: "rgba(69, 183, 209, 0.1)",
                        borderRadius: 2,
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(69, 183, 209, 0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#45B7D1",
                      },
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      py: 1.2,
                      color: "#1a1a1a",
                      "&::placeholder": {
                        color: "rgba(26, 26, 26, 0.3)",
                      },
                    },
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearSearch}
                    startIcon={<Clear />}
                    sx={{
                      color: "#45B7D1",
                      borderColor: "rgba(69, 183, 209, 0.2)",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "#45B7D1",
                        backgroundColor: "rgba(69, 183, 209, 0.04)",
                      },
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>

            {/* Table */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(69, 183, 209, 0.06)",
                backgroundColor: "#ffffff",
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>#</StyledTableCell>
                      <StyledTableCell>Instructor</StyledTableCell>
                      <StyledTableCell>Email</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Type</StyledTableCell>
                      <StyledTableCell align="center">Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userData.length > 0 ? (
                      userData.map((row, index) => (
                        <StyledTableRow key={row.id} hover>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              color: "rgba(26,26,26,0.4)",
                            }}
                          >
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  backgroundColor: "#45B7D1",
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: "#ffffff",
                                }}
                              >
                                {getInitials(row.firstname, row.lastname)}
                              </Avatar>
                              <Typography
                                sx={{ fontWeight: 500, color: "#1a1a1a" }}
                              >
                                {row.firstname + " " + row.lastname}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Email
                                sx={{
                                  fontSize: 14,
                                  color: "rgba(26,26,26,0.3)",
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "rgba(26,26,26,0.6)" }}
                              >
                                {row.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {row.status === 0 ? (
                              <Chip
                                label="Inactive"
                                size="small"
                                icon={<Block sx={{ fontSize: 14 }} />}
                                sx={{
                                  backgroundColor: "rgba(255, 107, 107, 0.1)",
                                  color: "#FF6B6B",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  height: 24,
                                }}
                              />
                            ) : (
                              <Chip
                                label="Active"
                                size="small"
                                icon={<Verified sx={{ fontSize: 14 }} />}
                                sx={{
                                  backgroundColor: "rgba(43, 173, 101, 0.1)",
                                  color: "#2BAD65",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  height: 24,
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.usertype || "Instructor"}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(69, 183, 209, 0.08)",
                                color: "#45B7D1",
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                height: 22,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="center"
                            >
                              {row.status === 0 ? (
                                <>
                                  <Tooltip title="Activate Instructor">
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={
                                        <CheckCircle sx={{ fontSize: 16 }} />
                                      }
                                      onClick={() => handleStatusActive(row.id)}
                                      sx={{
                                        backgroundColor: "#2BAD65",
                                        color: "#ffffff",
                                        fontSize: "0.65rem",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        "&:hover": {
                                          backgroundColor: "#239A56",
                                        },
                                      }}
                                    >
                                      Activate
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Delete Instructor">
                                    <IconButton
                                      onClick={() => handleDelete(row.id)}
                                      sx={{
                                        color: "#FF6B6B",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(255, 107, 107, 0.08)",
                                        },
                                      }}
                                    >
                                      <Delete sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <>
                                  <Tooltip title="Deactivate Instructor">
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={
                                        <Block sx={{ fontSize: 16 }} />
                                      }
                                      onClick={() =>
                                        handleStatusInactive(row.id)
                                      }
                                      sx={{
                                        backgroundColor: "#FF6B6B",
                                        color: "#ffffff",
                                        fontSize: "0.65rem",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        "&:hover": {
                                          backgroundColor: "#E55555",
                                        },
                                      }}
                                    >
                                      Deactivate
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Delete Instructor">
                                    <IconButton
                                      onClick={() => handleDelete(row.id)}
                                      sx={{
                                        color: "#FF6B6B",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(255, 107, 107, 0.08)",
                                        },
                                      }}
                                    >
                                      <Delete sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Stack>
                          </TableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <School
                            sx={{ fontSize: 48, color: "rgba(26,26,26,0.1)" }}
                          />
                          <Typography
                            sx={{ color: "rgba(26,26,26,0.3)", mt: 1 }}
                          >
                            {searchTerm
                              ? "No instructors found matching your search"
                              : "No instructors found"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid rgba(69, 183, 209, 0.06)",
                  "& .MuiTablePagination-select": {
                    color: "#1a1a1a",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    color: "rgba(26,26,26,0.4)",
                  },
                }}
              />
            </Paper>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default InstructorTable;
